// App router + layout shell. BottomNav handles navigation on mobile; on
// desktop (lg+) Sidebar takes over. Both share the same set of routes.
// Phase 31: also mounts the global Cmd/Ctrl+K Command Palette.
//
// Phase 35c: local-first auth — every visitor is automatically signed
// in as a local user. The Google sign-in is an opt-in card on the
// Settings page (not a separate route) so the entry point is
// discoverable without leaving the app. Phase 200 removed the
// /signin route entirely.
//
// Phase 36: language switcher — boots the active locale from the
// `language` setting (zh-Hant by default), exposes a LanguageProvider
// via context, and listens for cross-tab changes via localStorage
// 'storage' events.
//
// Performance: page components are lazy-loaded via React.lazy so the
// initial bundle ships only Dashboard + Auth + the shell. Budgets /
// Settings / Transactions / Add each become a separate chunk that
// downloads on first navigation. Cuts first-paint time on slow
// connections.
import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import BottomNav from './components/BottomNav.jsx';
import Sidebar from './components/Sidebar.jsx';
import TopBar from './components/TopBar.jsx';
import { FilterProvider } from './lib/filterContext.js';
import CommandPalette from './components/CommandPalette.jsx';
import OnboardingCarousel from './lib/onboard.jsx';
import Dashboard from './pages/Dashboard.jsx';
const Transactions = lazy(() => import('./pages/Transactions.jsx'));
const CardDetail = lazy(() => import('./pages/CardDetail.jsx'));
const Add = lazy(() => import('./pages/Add.jsx'));
const Budgets = lazy(() => import('./pages/Budgets.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
import { useAuth } from './lib/auth.jsx';
import { LanguageProvider, useLanguage, FALLBACK_LOCALE, resolve as resolveI18n } from './i18n/index.jsx';
import { db, settingsRepo } from './lib/localDb.js';
import { TopBarRightSlotProvider } from './lib/topbar-slot.jsx';
import { PrivacyProvider } from './lib/privacy.jsx';

// Title bar text per route, keyed by i18n key. If a key is missing the
// resolver falls back to the zh-Hant baseline, and finally to the key.
const TITLE_KEYS = {
  '/':             'dashboard.title',
  '/transactions': 'tx.title',
  '/add':          'add.newTitle',
  '/budgets':      'budgets.title',
  '/settings':     'settings.title',
};

function AppShell() {
  // Phase 92c: ALL hooks must be called unconditionally before any
  // early return (Rules of Hooks). Move the second useLocation() (was
  // on line 92, *after* the `if (loading) return …`) up here so the
  // hook count is identical across renders. Previously, when loading
  // transitioned from true → false, the second useLocation() call
  // appeared mid-component, React noticed the missing hook on the
  // first render, and unmounted the entire subtree — which is why
  // /transactions rendered 0 children in #root after Phase 92.
  const loc = useLocation();
  const { lang } = useLanguage();
  const title = TITLE_KEYS[loc.pathname]
    ? resolveI18n(TITLE_KEYS[loc.pathname], lang)
    : '';
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { loading } = useAuth();
  const hideTopBar = loc.pathname === '/budgets'
                  || loc.pathname === '/settings';

  // Phase 102: first-run onboarding trigger. Three guards + one
  // power-user escape hatch:
  //   1. user_prefs.onboarding.completed_at set → never show
  //   2. user_prefs.onboarding.dismissed_at set → never show
  //   3. tx_count >= 10 (heuristic: user has already figured it out)
  //   ?. ?skip=onboard in URL → skip silently (re-installs / dev)
  //   4. on /add → defer (Phase 188 M-115). New users opening the
  //      Add Transaction page directly hit the onboarding overlay
  //      blocking the modal, which made "add my first transaction"
  //      impossible without a long detour. The carousel fires on
  //      the next non-/add route instead. Same for /signin (the
  //      user is intentionally going there).
  useEffect(() => {
    if (loading) return;
    if (loc.pathname === '/add') return;
    if (new URLSearchParams(loc.search).get('skip') === 'onboard') {
      // Power-user escape hatch: clear URL, mark dismissed, never show.
      try {
        window.history.replaceState({}, '', loc.pathname);
        navigator.locks?.request?.('onboard-skip', async () => {});
      } catch (e) { /* no-op */ }
      db.user_prefs.put({
        key: 'onboarding',
        value: { completed_at: null, dismissed_at: new Date().toISOString(), seen_step: 0 },
        updated_at: new Date().toISOString(),
      }).catch(() => {});
      return;
    }
    (async () => {
      try {
        const pref = await db.user_prefs.get('onboarding');
        const v = pref?.value;
        if (!v) { setShowOnboarding(true); return; }
        // Phase 102b: TutorialReplaySection sets `force_show` when the
        // user explicitly taps the Replay button. This one-shot flag
        // supersedes both completed_at/dismissed_at and the tx>=10
        // auto-skip, so heavy users can still re-run the tutorial.
        if (v.force_show) {
          // Clear the flag — it's single-use.
          await db.user_prefs.put({
            key: 'onboarding',
            value: { ...v, force_show: null, updated_at: new Date().toISOString() },
            updated_at: new Date().toISOString(),
          });
          setShowOnboarding(true);
          return;
        }
        if (v.completed_at || v.dismissed_at) return;
        // Auto-skip heuristic: 10+ tx = user has figured it out.
        const txCount = await db.transactions.count();
        if (txCount >= 10) {
          await db.user_prefs.put({
            key: 'onboarding',
            value: { completed_at: new Date().toISOString(), dismissed_at: null, seen_step: 0 },
            updated_at: new Date().toISOString(),
          });
          return;
        }
        setShowOnboarding(true);
      } catch (e) {
        // DB read failed — fail open (don't show carousel) rather than
        // blocking the app. Log only.
        console.warn('[onboard] trigger check failed:', e);
      }
    })();
  }, [loading, loc.pathname, loc.search]);

  // Cmd/Ctrl+K opens the command palette from anywhere.
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(o => !o);
      } else if (e.key === '/' && !['INPUT','TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // While AuthProvider is finishing local seeding, show a thin loading
  // screen instead of flashing the dashboard.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-slate-400 text-sm">{resolveI18n('app.loading', lang)}</div>
      </div>
    );
  }

  // Phase 35c + Phase 200: every visitor is auto-signed in as a local
  // user (see auth.jsx). The Google sign-in entry point is the
  // Account binding card on the Settings page (no /signin route). If
  // somehow user is null (very early render race), default to
  // dashboard.

  // Phase 76f: hide the global TopBar on Budgets + Settings.

  return (
    <FilterProvider>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {!hideTopBar && (
        <TopBar title={title} onSearchClick={openPalette} />
      )}
      <div className="lg:flex">
        <Sidebar />
        <main className={[
          'flex-1 min-w-0 max-w-3xl mx-auto px-4 pb-24 sm:pb-6',
          // When the TopBar is hidden, drop the top padding down a
          // touch and start a bit lower so the page title has air.
          hideTopBar ? 'pt-6' : 'pt-4',
        ].join(' ')}>
          <Suspense fallback={<RouteFallback lang={lang} />}>
            <Routes>
              <Route path="/"             element={<Dashboard />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/cards/:local_uuid" element={<CardDetail />} />
              <Route path="/add"          element={<Add />} />
              <Route path="/budgets"      element={<Budgets />} />
              <Route path="/settings"     element={<Settings />} />
              <Route path="*"             element={<NotFound lang={lang} />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      <BottomNav />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {showOnboarding && (
        <OnboardingCarousel onComplete={() => setShowOnboarding(false)} />
      )}
    </div>
    </FilterProvider>
  );
}

function NotFound({ lang }) {
  return (
    <div className="p-12 text-center">
      <h1 className="text-2xl font-bold text-slate-900">404</h1>
      <p className="mt-2 text-slate-500">頁面唔存在</p>
    </div>
  );
}

// Shown while a lazy route chunk is being fetched on first visit.
// Matches the page-shell padding so the layout doesn't jump.
function RouteFallback({ lang }) {
  return (
    <div className="py-12 text-center text-slate-400 text-sm">{resolveI18n('app.loading', lang)}</div>
  );
}

export default function App() {
  const [lang, setLang] = useState(FALLBACK_LOCALE);
  // Read the saved language once on boot. Falls back to zh-Hant for
  // users on older DBs that pre-date the `language` setting, or if the
  // IndexedDB read fails (e.g. private browsing quota errors).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const saved = await settingsRepo.get('language');
        if (alive && saved) setLang(saved);
      } catch (e) {
        // ignore — keep fallback locale
      }
    })();
    return () => { alive = false; };
  }, []);

  // Cross-tab sync: when settings.jsx writes a new language to
  // settingsRepo AND to localStorage('finance_app_lang'), other tabs
  // pick it up via the 'storage' event. Same tab updates flow through
  // the React state setter directly (no event needed).
  useEffect(() => {
    const onChange = (e) => {
      if (e.key === 'finance_app_lang' && e.newValue) {
        setLang(e.newValue);
      }
    };
    window.addEventListener('storage', onChange);
    return () => window.removeEventListener('storage', onChange);
  }, []);

  return (
    <LanguageProvider lang={lang} setLang={setLang}>
      <TopBarRightSlotProvider>
        <PrivacyProvider>
        <AppShell />
        </PrivacyProvider>
      </TopBarRightSlotProvider>
    </LanguageProvider>
  );
}

// trigger-rebuild 2026-08-25 18:30 HKT — force Render to pick up linear HEAD
